/**
 * CR220: Safe Value Rendering Utilities
 * 
 * Provides utilities to prevent "undefined", "unknown", or similar 
 * invalid strings from appearing in rendered deal cards.
 * 
 * Rules:
 * 1. Never render raw "undefined", "null", "unknown", "[object Object]"
 * 2. Hide blocks when required fields are missing (preferred)
 * 3. Use explicit fallbacks only when configured
 */

// ================================================================
// UNSAFE VALUE DETECTION
// ================================================================

/**
 * Values that should NEVER appear in the UI
 */
const UNSAFE_VALUES = [
  "undefined",
  "null", 
  "unknown",
  "[object Object]",
  "NaN",
  "Invalid Date",
  "",
];

/**
 * Check if a value is "safe" for rendering.
 * Returns false for null, undefined, or any string in UNSAFE_VALUES.
 */
export function isSafeValue(value: unknown): value is string | number | boolean {
  if (value === null || value === undefined) {
    return false;
  }
  
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return !UNSAFE_VALUES.includes(normalized) && value.trim().length > 0;
  }
  
  if (typeof value === "number") {
    return !isNaN(value) && isFinite(value);
  }
  
  if (typeof value === "boolean") {
    return true;
  }
  
  // Objects, arrays, etc. are not safe for direct rendering
  return false;
}

/**
 * Get a safe string value or return null.
 * Use this to conditionally render text.
 */
export function getSafeString(value: unknown): string | null {
  if (!isSafeValue(value)) {
    return null;
  }
  return String(value);
}

/**
 * Get a safe string with a fallback.
 * Only use explicit fallbacks when they add value (e.g., "N/A" in tables).
 */
export function getSafeStringOr(value: unknown, fallback: string): string {
  const safe = getSafeString(value);
  return safe ?? fallback;
}

// ================================================================
// BLOCK VISIBILITY
// ================================================================

export interface BlockVisibilityRule {
  /** Unique block identifier */
  blockId: string;
  /** Fields required for this block to render */
  requiredFields: string[];
  /** Optional custom visibility check */
  visibleWhen?: (deal: Record<string, unknown>) => boolean;
}

/**
 * Default visibility rules for deal card blocks.
 * These define which fields must be present for each block to render.
 */
export const DEFAULT_BLOCK_VISIBILITY_RULES: BlockVisibilityRule[] = [
  {
    blockId: "merchant_name",
    requiredFields: ["merchant", "promo_merchant_name"],
  },
  {
    blockId: "headline",
    requiredFields: ["headline_mechanic"],
  },
  {
    blockId: "gift_card_brand",
    requiredFields: ["purchase_gift_card_brand", "gift_card_brand", "reward_brand_name"],
  },
  {
    blockId: "bonus_value",
    requiredFields: ["bonus_amount"],
    visibleWhen: (deal) => deal.deal_category === "BONUS" || deal.deal_type === "bonus",
  },
  {
    blockId: "discount_value",
    requiredFields: ["discount_percent"],
    visibleWhen: (deal) => deal.deal_category === "DISCOUNT" || deal.deal_type === "discount",
  },
  {
    blockId: "points_value",
    requiredFields: ["points_amount"],
    visibleWhen: (deal) => deal.deal_category === "POINTS" || deal.deal_type === "points",
  },
  {
    blockId: "validity_dates",
    requiredFields: ["start_date", "end_date"],
  },
  {
    blockId: "source_indicator",
    requiredFields: ["source_type"],
  },
  {
    blockId: "fine_print",
    requiredFields: ["fine_print"],
  },
  {
    blockId: "claim_url",
    requiredFields: ["claim_url"],
  },
];

/**
 * Check if a block should be visible based on deal data.
 * Returns true if ANY required field has a safe value (OR logic),
 * AND the custom visibleWhen check passes (if defined).
 */
export function isBlockVisible(
  deal: Record<string, unknown>,
  rule: BlockVisibilityRule
): boolean {
  // Check custom visibility function first
  if (rule.visibleWhen && !rule.visibleWhen(deal)) {
    return false;
  }
  
  // At least one required field must have a safe value
  return rule.requiredFields.some((field) => isSafeValue(deal[field]));
}

/**
 * Get visibility map for all blocks given a deal.
 */
export function getBlockVisibilityMap(
  deal: Record<string, unknown>,
  rules: BlockVisibilityRule[] = DEFAULT_BLOCK_VISIBILITY_RULES
): Record<string, boolean> {
  const map: Record<string, boolean> = {};
  for (const rule of rules) {
    map[rule.blockId] = isBlockVisible(deal, rule);
  }
  return map;
}

// ================================================================
// DEAL VALIDATION FOR PREVIEW
// ================================================================

/**
 * Minimum fields required for a deal to be "preview-ready"
 * (i.e., won't show undefined values in critical areas)
 */
const MINIMUM_REQUIRED_FIELDS = [
  "merchant",
  "headline_mechanic",
];

/**
 * Check if a deal meets minimum requirements for preview rendering.
 */
export function isDealPreviewReady(deal: Record<string, unknown>): boolean {
  return MINIMUM_REQUIRED_FIELDS.every((field) => isSafeValue(deal[field]));
}

/**
 * Filter deals to only those that are preview-ready.
 * Use this when "Show only valid deals" is enabled.
 */
export function filterPreviewReadyDeals<T extends Record<string, unknown>>(
  deals: T[]
): T[] {
  return deals.filter(isDealPreviewReady);
}

// ================================================================
// TEXT SANITIZATION
// ================================================================

/**
 * Sanitize a string for display, removing or replacing unsafe patterns.
 */
export function sanitizeDisplayText(text: unknown): string | null {
  if (!isSafeValue(text)) {
    return null;
  }
  
  let result = String(text);
  
  // Remove patterns like "(undefined)" or "/ undefined"
  result = result.replace(/\s*\(\s*undefined\s*\)/gi, "");
  result = result.replace(/\s*\/\s*undefined/gi, "");
  result = result.replace(/undefined\s*\/\s*/gi, "");
  result = result.replace(/\s*-\s*undefined/gi, "");
  result = result.replace(/undefined\s*-\s*/gi, "");
  
  // Clean up whitespace
  result = result.replace(/\s+/g, " ").trim();
  
  // If nothing meaningful remains, return null
  if (!result || UNSAFE_VALUES.includes(result.toLowerCase())) {
    return null;
  }
  
  return result;
}

// ================================================================
// COMPOSITE VALUE HELPERS
// ================================================================

/**
 * Build a merchant display string from available fields.
 * Prefers promo_merchant_name, falls back to merchant.
 */
export function getMerchantDisplay(deal: Record<string, unknown>): string | null {
  return getSafeString(deal.promo_merchant_name) || getSafeString(deal.merchant);
}

/**
 * Build a gift card brand display string.
 */
export function getGiftCardBrandDisplay(deal: Record<string, unknown>): string | null {
  return getSafeString(deal.purchase_gift_card_brand) 
    || getSafeString(deal.gift_card_brand)
    || getSafeString(deal.reward_brand_name);
}

/**
 * Build a source display string.
 */
export function getSourceDisplay(deal: Record<string, unknown>): string | null {
  const sourceType = getSafeString(deal.source_type);
  if (!sourceType) return null;
  
  // Capitalize first letter
  return sourceType.charAt(0).toUpperCase() + sourceType.slice(1);
}
